import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { completeReplenishmentTask } from "@/lib/replenishment-auto.functions";
import {
  listWorkerReplenishmentQueue,
  claimReplenishmentTask,
  releaseReplenishmentClaim,
} from "@/lib/replenishment-claim.functions";
import {
  scanReplenishmentSource,
  scanReplenishmentForward,
  attachReplenishmentLpn,
  checkReplenishmentReady,
} from "@/lib/replenishment-scan.functions";
import { RfShell, RfEmpty, RfScopeMissing } from "@/components/rf/rf-shell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useScope } from "@/hooks/use-scope";
import { toast } from "sonner";
import { CheckCircle2, Hand, ScanLine, Package } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/rf/replenishment")({
  component: RfReplenishment,
});

function RfReplenishment() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listWorkerReplenishmentQueue);
  const claimFn = useServerFn(claimReplenishmentTask);
  const releaseFn = useServerFn(releaseReplenishmentClaim);
  const completeFn = useServerFn(completeReplenishmentTask);
  const scanSourceFn = useServerFn(scanReplenishmentSource);
  const scanForwardFn = useServerFn(scanReplenishmentForward);
  const attachLpnFn = useServerFn(attachReplenishmentLpn);
  const checkReadyFn = useServerFn(checkReplenishmentReady);

  const { data: tasks = [], isLoading, refetch } = useQuery({
    queryKey: ["rf-replen-queue", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
    refetchInterval: 15_000,
  });

  const [activeId, setActiveId] = useState<string | null>(null);
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanInput, setScanInput] = useState("");
  const [lpnInput, setLpnInput] = useState("");
  const active: any = useMemo(
    () => (tasks as any[]).find((t) => t.id === activeId) ?? null,
    [tasks, activeId],
  );

  if (!tenantId || !facilityId) {
    return <RfShell title="Replenishment" back><RfScopeMissing /></RfShell>;
  }

  async function claim(id: string, qtyRequested: number) {
    setBusy(true);
    try {
      await claimFn({ data: { taskId: id } });
      setActiveId(id);
      setQty(String(qtyRequested));
      await refetch();
    } catch (e: any) {
      toast.error(e?.message ?? "Claim failed");
      await refetch();
    } finally { setBusy(false); }
  }

  async function release() {
    if (!active) return;
    setBusy(true);
    try {
      await releaseFn({ data: { taskId: active.id } });
      setActiveId(null); setQty("");
      await refetch();
    } catch (e: any) {
      toast.error(e?.message ?? "Release failed");
    } finally { setBusy(false); }
  }

  async function confirm() {
    if (!active) return;
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Enter a positive qty");
    setBusy(true);
    try {
      const ready = await checkReadyFn({ data: { taskId: active.id } });
      if (!ready.ready) {
        toast.error(`Missing: ${ready.missing.join(", ")}`);
        setBusy(false);
        return;
      }
      await completeFn({ data: { id: active.id, qtyCompleted: n } });
      toast.success(`Replenished ${n}`);
      setActiveId(null); setQty(""); setScanInput(""); setLpnInput("");
      await refetch();
      qc.invalidateQueries({ queryKey: ["rf-replen-queue"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Complete failed");
    } finally { setBusy(false); }
  }

  async function scanSource() {
    if (!active || !scanInput) return;
    setBusy(true);
    try {
      await scanSourceFn({ data: { taskId: active.id, locationCode: scanInput } });
      toast.success("Source confirmed");
      setScanInput("");
      await refetch();
    } catch (e: any) { toast.error(e?.message ?? "Scan failed"); }
    finally { setBusy(false); }
  }

  async function scanForward() {
    if (!active || !scanInput) return;
    setBusy(true);
    try {
      await scanForwardFn({ data: { taskId: active.id, locationCode: scanInput } });
      toast.success("Drop confirmed");
      setScanInput("");
      await refetch();
    } catch (e: any) { toast.error(e?.message ?? "Scan failed"); }
    finally { setBusy(false); }
  }

  async function attachLpn() {
    if (!active || !lpnInput) return;
    setBusy(true);
    try {
      await attachLpnFn({ data: { taskId: active.id, lpnCode: lpnInput } });
      toast.success("LPN attached");
      setLpnInput("");
      await refetch();
    } catch (e: any) { toast.error(e?.message ?? "LPN failed"); }
    finally { setBusy(false); }
  }

  if (active) {
    const sourceDone = !!active.source_scanned_at;
    const forwardDone = !!active.forward_scanned_at;
    const lpnDone = !!active.lpn_id;
    return (
      <RfShell title="Replenish" back="/app/rf/replenishment">
        <div className="rounded-md border bg-muted/30 p-3 space-y-1 text-sm">
          <div className="text-xs text-muted-foreground">Move</div>
          <div className="font-mono">{active.qty_requested} units</div>
          <div className="text-[11px] text-muted-foreground">Task {active.id.slice(0, 8)}</div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <span className={sourceDone ? "text-emerald-600" : "text-muted-foreground"}>
              {sourceDone ? "✓" : "○"} source
            </span>
            <span className={lpnDone ? "text-emerald-600" : "text-muted-foreground"}>
              {lpnDone ? "✓" : "○"} lpn
            </span>
            <span className={forwardDone ? "text-emerald-600" : "text-muted-foreground"}>
              {forwardDone ? "✓" : "○"} drop
            </span>
          </div>
          <div className="flex gap-2">
            <Input value={scanInput} onChange={(e) => setScanInput(e.target.value)}
              placeholder="Scan location code" className="font-mono" />
            {!sourceDone ? (
              <Button variant="outline" onClick={scanSource} disabled={busy || !scanInput}>
                <ScanLine className="h-4 w-4 mr-1" />Source
              </Button>
            ) : (
              <Button variant="outline" onClick={scanForward} disabled={busy || !scanInput}>
                <ScanLine className="h-4 w-4 mr-1" />Drop
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Input value={lpnInput} onChange={(e) => setLpnInput(e.target.value)}
              placeholder="Scan LPN / tote (optional)" className="font-mono" />
            <Button variant="outline" onClick={attachLpn} disabled={busy || !lpnInput}>
              <Package className="h-4 w-4 mr-1" />LPN
            </Button>
          </div>
        </div>

        <Input inputMode="numeric" pattern="[0-9]*" autoFocus value={qty}
          onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))}
          className="h-14 text-2xl text-center font-mono" placeholder={String(active.qty_requested)} />
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={release} disabled={busy}>Release</Button>
          <Button onClick={confirm} disabled={busy || !qty}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> Complete
          </Button>
        </div>
      </RfShell>
    );
  }

  return (
    <RfShell title="Replenishment" back>
      <div className="text-xs text-muted-foreground px-1">
        {isLoading ? "Loading…" : `${tasks.length} task${tasks.length === 1 ? "" : "s"} in queue`}
      </div>
      <div className="space-y-2">
        {tasks.length === 0 && !isLoading ? (
          <RfEmpty>Queue is empty. Nice work.</RfEmpty>
        ) : (
          (tasks as any[]).map((t) => {
            const mine = !!t.claimed_by;
            return (
              <button key={t.id} type="button" disabled={busy}
                onClick={() => mine ? setActiveId(t.id) : claim(t.id, t.qty_requested)}
                className="flex w-full items-center gap-3 rounded-md border bg-card px-3 py-3 text-left active:bg-muted/70 disabled:opacity-60">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-mono text-sm">{t.id.slice(0, 8)}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                    {mine ? (
                      <><Hand className="h-3 w-3" /> claimed · {Math.round(t.age_minutes ?? 0)}m old</>
                    ) : (
                      <>open · {Math.round(t.age_minutes ?? 0)}m old</>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-base font-semibold tabular-nums">{t.qty_requested}</div>
                  <div className="text-[10px] text-muted-foreground">qty</div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </RfShell>
  );
}